/* ═══════════════════════════════════════════════════════════════════════════
   EmptyVisual — the shared "visual empty" primitive (Flat Grammar 2027).

   A glyph composition carries the meaning: either an icon in a tonal circle,
   or a SHAPE HINT that previews what will live there (ghost rows, ghost bars,
   a ghost grid, a mini month, overlapping avatars). Pure CSS: tonal fills at
   `hsl(var(--muted) / 0.5)`, no borders, no shadows, tokens only so dark mode
   comes for free.

   Every dimension derives from `--ev-u`, so the whole composition scales with
   one number per size.
   ═══════════════════════════════════════════════════════════════════════════ */

.ev {
  /* The ground the composition sits on — the avatar rings and the glyph badge
     punch through the ghosts with it. Override on a surface that is not the
     page background (e.g. `style={{ '--ev-surface': 'hsl(var(--card))' }}`). */
  --ev-surface: hsl(var(--background));
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ev-page {
  --ev-u: 7px;
  gap: 1.125rem;
  padding: 3.5rem 1.25rem;
}

.ev-card {
  --ev-u: 5.5px;
  gap: 0.75rem;
  padding: 2rem 1rem;
}

.ev-strip {
  --ev-u: 3px;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  text-align: left;
}

.ev-fullscreen {
  min-height: 60vh;
  justify-content: center;
}

/* ── The shape frame ─────────────────────────────────────────────────────── */

.ev-shape {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ev-breathe 1.2s ease-in-out infinite alternate;
}

@media (prefers-reduced-motion: reduce) {
  .ev-shape {
    animation: none;
  }
}

@keyframes ev-breathe {
  from {
    opacity: 0.62;
  }
  to {
    opacity: 1;
  }
}

/* Every ghost element shares one tonal fill and no edge. */
.ev-ghost {
  background: hsl(var(--muted) / 0.5);
}

/* ── Icon: a tonal circle, the fallback when there is nothing to preview ─── */

.ev-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: hsl(var(--muted) / 0.6);
  color: hsl(var(--foreground) / 0.7);
  width: calc(var(--ev-u) * 6.7);
  height: calc(var(--ev-u) * 6.7);
}

.ev-card .ev-circle {
  width: calc(var(--ev-u) * 6.2);
  height: calc(var(--ev-u) * 6.2);
}

/* ── Small glyph riding the corner of a shape hint ───────────────────────── */

.ev-glyph {
  position: absolute;
  right: calc(var(--ev-u) * -2.2);
  bottom: calc(var(--ev-u) * -1.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: hsl(var(--muted));
  color: hsl(var(--foreground) / 0.7);
  box-shadow: 0 0 0 calc(var(--ev-u) * 0.42) var(--ev-surface);
  width: calc(var(--ev-u) * 3.6);
  height: calc(var(--ev-u) * 3.6);
}

/* ── rows: three ghost rows with a leading circle ────────────────────────── */

.ev-rows {
  flex-direction: column;
  align-items: stretch;
  gap: calc(var(--ev-u) * 1.2);
  width: calc(var(--ev-u) * 14);
}

.ev-row {
  display: flex;
  align-items: center;
  gap: calc(var(--ev-u) * 1.1);
}

.ev-row-dot {
  flex: none;
  width: calc(var(--ev-u) * 1.7);
  height: calc(var(--ev-u) * 1.7);
  border-radius: 999px;
}

.ev-row-bar {
  height: calc(var(--ev-u) * 1.4);
  border-radius: 999px;
}

.ev-row:nth-child(1) .ev-row-bar {
  width: 100%;
}
.ev-row:nth-child(2) .ev-row-bar {
  width: 76%;
}
.ev-row:nth-child(3) .ev-row-bar {
  width: 54%;
}

/* ── bars: three ghost bars of different heights ─────────────────────────── */

.ev-bars {
  gap: calc(var(--ev-u) * 1.4);
  height: calc(var(--ev-u) * 8);
}

.ev-bar {
  width: calc(var(--ev-u) * 2.4);
  border-radius: calc(var(--ev-u) * 0.7);
}

.ev-bar:nth-child(1) {
  height: 54%;
}
.ev-bar:nth-child(2) {
  height: 100%;
}
.ev-bar:nth-child(3) {
  height: 72%;
}

/* ── grid: a 3×3 ghost grid ──────────────────────────────────────────────── */

.ev-grid {
  display: grid;
  grid-template-columns: repeat(3, calc(var(--ev-u) * 3.4));
  gap: calc(var(--ev-u) * 1);
}

.ev-cell {
  height: calc(var(--ev-u) * 3.4);
  border-radius: calc(var(--ev-u) * 0.9);
}

/* ── calendar: a mini month ──────────────────────────────────────────────── */

.ev-cal {
  flex-direction: column;
  align-items: stretch;
  gap: calc(var(--ev-u) * 0.9);
  width: calc(var(--ev-u) * 14);
}

.ev-cal-head {
  height: calc(var(--ev-u) * 1.5);
  width: 55%;
  border-radius: 999px;
}

.ev-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: calc(var(--ev-u) * 0.55);
}

.ev-cal-cell {
  aspect-ratio: 1;
  border-radius: calc(var(--ev-u) * 0.35);
}

/* ── avatars: three overlapping circles ──────────────────────────────────── */

.ev-avatars {
  align-items: center;
}

.ev-av {
  width: calc(var(--ev-u) * 4.2);
  height: calc(var(--ev-u) * 4.2);
  border-radius: 999px;
  /* A ring in the surface colour, not a drop shadow — it reads as the gap
     between overlapping circles. */
  box-shadow: 0 0 0 calc(var(--ev-u) * 0.4) var(--ev-surface);
}

.ev-av + .ev-av {
  margin-left: calc(var(--ev-u) * -1.5);
}

/* ── strip: one line, a small tonal mark instead of a composition ────────── */

.ev-mark {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: calc(var(--ev-u) * 2);
  background: hsl(var(--muted) / 0.6);
  color: hsl(var(--foreground) / 0.7);
  width: calc(var(--ev-u) * 6.5);
  height: calc(var(--ev-u) * 6.5);
}

.ev-mark-bars {
  display: flex;
  flex-direction: column;
  gap: calc(var(--ev-u) * 0.7);
  width: calc(var(--ev-u) * 3);
}

.ev-mark-bar {
  height: calc(var(--ev-u) * 0.9);
  border-radius: 999px;
  background: hsl(var(--foreground) / 0.25);
}

.ev-mark-bar:last-child {
  width: 62%;
}

/* ── Copy + action ───────────────────────────────────────────────────────── */

.ev-line {
  margin: 0;
  max-width: 26ch;
  text-wrap: balance;
  line-height: 1.35;
}

.ev-page .ev-line {
  font-size: 0.9375rem;
  font-weight: 500;
  color: hsl(var(--foreground));
}

.ev-card .ev-line {
  font-size: 0.8125rem;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
}

.ev-strip .ev-line {
  max-width: none;
  font-size: 0.8125rem;
  color: hsl(var(--muted-foreground));
}

/* One muted hint line under the line — the next step, never a paragraph. */
.ev-hint {
  margin: 0;
  max-width: 30ch;
  text-wrap: balance;
  line-height: 1.35;
  font-size: 0.8125rem;
  color: hsl(var(--muted-foreground));
}

.ev-card .ev-hint,
.ev-strip .ev-hint {
  font-size: 0.75rem;
}

/* Tonal action — never outlined, never more than one. */
.ev-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2rem;
  padding: 0 0.875rem;
  border: 0;
  border-radius: 999px;
  background: hsl(var(--muted) / 0.7);
  color: hsl(var(--foreground));
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}

.ev-action:hover {
  background: hsl(var(--muted));
}

.ev-action:disabled {
  cursor: default;
  opacity: 0.6;
}

.ev-strip .ev-action {
  height: 1.625rem;
  padding: 0 0.625rem;
  font-size: 0.75rem;
}
